import { createBrowserRouter, Navigate, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { EstadoCargando, EstadoError } from '@xiltrion/ui/bloques/estados';
import { apiGet } from '@/api/client';
import { AppShell } from './AppShell';
import { registry } from './extension-points';
import { useSession } from './session';
import { Ingreso } from '@/core/auth/Ingreso';
import { Home } from '@/core/home/Home';
import { MyTasks } from '@/core/projects/MyTasks';
import { ProjectsList } from '@/core/projects/ProjectsList';
import { ProjectPage } from '@/core/projects/ProjectPage';
import { ReviewInbox } from '@/core/decisions/ReviewInbox';
import { Profile } from '@/core/profile/Profile';
import { NotificationsPage } from '@/core/notifications/NotificationsPage';
import { coreRoutes } from '@/core/routes';

/** `/tareas/:id` lleva al proyecto de la tarea con el panel abierto. */
function TaskRedirect() {
  const { taskId } = useParams();
  const { data, error } = useQuery({ queryKey: ['task-redirect', taskId], queryFn: () => apiGet<{ projectId: string }>(`/tasks/${taskId}`) });
  if (error) return <div className="p-6"><EstadoError error={error} /></div>;
  if (!data) return <div className="p-6"><EstadoCargando /></div>;
  return <Navigate to={`/proyectos/${data.projectId}?tarea=${taskId}`} replace />;
}

/** Rutas de los paquetes: solo existen si su módulo está activo. */
function ModuleGate({ module, children }: { module: string; children: React.ReactNode }) {
  const { has } = useSession();
  return has(module) ? <>{children}</> : <div className="p-6"><EstadoError titulo="Esta función no está activa en tu organización." /></div>;
}

export function buildRouter() {
  return createBrowserRouter([
    // El ingreso es el de la suite (va directo a `/auth/oidc/start`): Planning no tiene pantalla de ingreso propia.
    { path: '/ingreso', element: <Ingreso /> },
    ...registry.routes().filter((r) => r.path.startsWith('/publico')).map((r) => ({ path: r.path, element: r.element })),
    {
      path: '/',
      element: <AppShell />,
      children: [
        { index: true, element: <Home /> },
        { path: 'mis-tareas', element: <MyTasks /> },
        { path: 'proyectos', element: <ProjectsList /> },
        { path: 'proyectos/:projectId', element: <ProjectPage /> },
        { path: 'tareas/:taskId', element: <TaskRedirect /> },
        { path: 'bandeja', element: <ReviewInbox /> },
        { path: 'perfil', element: <Profile /> },
        { path: 'avisos', element: <NotificationsPage /> },
        ...coreRoutes(),
        ...registry.routes().filter((r) => !r.path.startsWith('/publico')).map((r) => ({ path: r.path.replace(/^\//, ''), element: <ModuleGate module={r.module}>{r.element}</ModuleGate> })),
        { path: '*', element: <div className="p-6"><EstadoError titulo="Esta página no existe." /></div> },
      ],
    },
  ]);
}
